import React, {useRef, useState} from "react";
import {ResponsiveContent} from "../../ResponsiveNavTabs";
import {Button, ListGroup, Stack} from "react-bootstrap";
import {Link, useParams} from "react-router-dom";
import {
    useAddressOrderOptions,
    useLocalFilter2,
    useLongPress,
    useSelect,
    useTouch,
    useAddressCount,
    useAddressList, useFetchJson
} from "../../../hooks";
import {AdminUserAlert, ItemLoading, MenuEditButton} from "../../_components";
import {useAlert, useAuth} from "../../../context";
import {OurPagination2} from "../../_components";
import {MenuAdd, MenuBar, MenuClearSelectButton, MenuConfirmDeleteButton, MenuOrderBy, MenuSearchTerm, OurClearAlert, PageTitle} from "../../_components";
import {endpoints} from "../../../utils2";
import {useBrowserTitle} from "../../../hooks/useBrowserTitle";


export function Address() {
    useBrowserTitle("Address");
    return <ResponsiveContent body={<AddressBody/>}/>
}

function AddressBody() {
    const {authToken, setVatPercentage} = useAuth()
    const {uid} = useParams()
    const {success} = useAlert()

    // Get order options
    const {orderOptions, errorOrderOptions, clearErrorOrderOptions} = useAddressOrderOptions()

    // API count all items
    const {count, setCount, loadingCount, errorCount, clearErrorCount} = useAddressCount()

    // API items per page
    const {list, setList, loadingList, errorList, clearErrorList} = useAddressList(10)

    // Filter
    const filteredList = useLocalFilter2(list, ["line1", "line2", "zip", "city", "country"])

    // Select
    const [selList, toggleSelectItems, clearSelectItems, isItemSelected] = useSelect(list)

    const {exec: deleteMany, error: errDeleteMany, clearError: clearErrDeleteMany} = useFetchJson(authToken?.jwt, null, "POST")

    function handleOnItemsDelete() {
        const payload = {addresses: selList.map(address => ({id: address.id}))}
        deleteMany(endpoints.address.deletes(uid), payload, resp => {
            const ids = resp.data // payload.addresses.map(a => a.id)
            success(`Removed ${ids.length} address(s)!`)
            setCount(count => count - selList.length)
            setList(prev => prev.filter(obj => !ids.includes(obj.id)))

            getUserVat(endpoints.user.vat(uid), null, resp => {
                setVatPercentage(resp.data.vat_percentage, true)
            })
        })

        // NOTE. The following example can be used to update user's UI at the end of a multiple backend iteration
        //
        // Promise.all(selList.map(item =>
        //     deleteItem(item.id).then(() => Promise.resolve(item.id)).catch(err => console.warn(err))
        // )).then(ids => {
        //     success(`Removed ${ids.length} address(s)!`)
        //     setCount(count => count - ids.length)
        //     setList(prev => prev.filter(obj => !obj.id.includes(ids)))
        //
        //     getUserVat(endpoints.user.vat(uid), null, resp => {
        //         setVatPercentage(resp.data.vat_percentage, true)
        //     })
        //     clearSelectItems()
        // })
    }


    const {exec: sendAddressJson, error: errAddress, clearError: clearErrAddress} = useFetchJson(authToken?.jwt, null, "PUT")
    const {exec: getUserVat, error: errUserVat, clearError: clearErrUserVat} = useFetchJson(authToken?.jwt, null, "GET")

    function handleSetMainAddress(item) {
        const payload = {main: true}
        sendAddressJson(endpoints.address.edit(uid, item.id), payload, resp => {
            setList(prev => prev.map(obj => (obj.id !== resp.data.id) ? {...obj, main: false} : {...obj, main: true}))

            getUserVat(endpoints.user.vat(uid), null, resp => {
                setVatPercentage(resp.data.vat_percentage, true)
            })
        })
    }

    return <Stack>

        <AdminUserAlert/>
        <OurClearAlert variant="danger" msg={errorOrderOptions} onClear={clearErrorOrderOptions}/>
        <OurClearAlert variant="danger" msg={errorCount} onClear={clearErrorCount}/>
        <OurClearAlert variant="danger" msg={errorList} onClear={clearErrorList}/>
        <OurClearAlert variant="danger" msg={errAddress} onClear={clearErrAddress}/>
        <OurClearAlert variant="danger" msg={errUserVat} onClear={clearErrUserVat}/>

        <PageTitle title="Address">Manage {filteredList.length} of {count} addresses.</PageTitle>

        <MenuBar>
            <MenuAdd to={`/backend/address/${uid}/add`} icon="plus">Add</MenuAdd>
            <MenuSearchTerm/>
            <MenuOrderBy orderOptions={orderOptions}/>
            <MenuEditButton hide={selList.length !== 1} onClick={() => handleSetMainAddress(selList[0])}>
                Set Main
            </MenuEditButton>
            <MenuClearSelectButton hide={selList.length === 0} onClick={clearSelectItems}>
                {selList.length} {selList.length === 1 ? "row" : "rows"}
            </MenuClearSelectButton>
            <MenuConfirmDeleteButton hide={selList.length === 0} onSubmit={handleOnItemsDelete}>
                <span>{selList.length} {selList.length === 1 ? "address" : "addresses"}</span>
            </MenuConfirmDeleteButton>
        </MenuBar>

        <ItemLoading show={loadingCount || loadingList}/>

        {list && list.length > 0 ?
            <ListGroup variant="flush">
                {filteredList.map(item =>
                    <RenderItem key={item.id}
                                item={item}
                                selected={isItemSelected(item.id)}
                                selectedCount={selList.length}
                                onSelect={toggleSelectItems}
                                to={`/backend/address/${uid}/${item.id}`}/>
                )}
            </ListGroup>
            :
            <span className="text-center">No address added yet</span>
        }

        <OurPagination2 loading={loadingCount || loadingList} itemCount={count}/>
    </Stack>
}

function RenderItem({item, selected, selectedCount, onSelect, to}) {

    const touch = useTouch()
    const [show, setShow] = useState(false)

    const ref = useRef(null)
    useLongPress(ref, () => onSelect(item.id))

    return (
        // <Stack direction="horizontal" gap="2" className={`mb-1 rounded ${selected && "bg-light"} ${show && "bg-light-subtle"}`}
        <Stack direction="horizontal" gap="2" className={`mb-1 rounded ${(selected || show) && "bg-light-subtle"}`}
               onMouseEnter={() => setShow(true)}
               onMouseLeave={() => setShow(false)}>
            <Stack ref={ref} direction="horizontal" className="flex-grow-1" gap="2">
                <div>
                    {/*<i className="bi bi-flag fs-3"/>*/}
                    <i className={`bi bi-geo-alt fs-2 ${item.main ? "text-success" : ""}`}/>
                </div>
                <div>
                    <Stack direction="vertical" className="" gap={0}>
                        <Link to={to}
                              className="flex-shrink-0 link-body-emphasis link-underline link-underline-opacity-0 link-underline-opacity-75-hover link-offset-1 link-offset-1-hover">
                            <span className="text-wrap fw-semibold fs-4">{item.name}</span>
                        </Link>
                        <span className="text-wrap fs-6 fw-light">{item.line1}</span>
                        <span className="text-wrap fs-6 fw-light">{`${item.city} ${item.zip}`}</span>
                    </Stack>
                </div>
                <div className="ms-auto">
                    <Stack className="text-body-tertiary align-items-end justify-content-center" gap={0}>
                        <span className="text-nowrap fw-normal text-secondary">{item.phone}</span>
                        <span className="text-nowrap fw-normal text-secondary">{`${item.region} ${item.country}`}</span>
                    </Stack>
                </div>
            </Stack>

            <div className="ms-auto">
                {(!touch || selected || selectedCount > 0) &&
                    <Button className="link-underline link-underline-opacity-0" variant="link" onClick={() => onSelect(item.id)}>
                        <span className={`bi ${selected ? "bi-check-square" : "bi-square"}`}></span>
                    </Button>
                }
            </div>
        </Stack>
    )
}